import type {
    ContractOtherCostLimitInput,
    ContractOtherCostLimitSpanInput,
    ContractOtherCostLimitSpanSeasonInput,
    ContractOtherCostSeasonLimitInput,
} from 'gql/graphql';
import { replacerCost } from 'utils/replacerCost';

import * as R from 'ramda';

import type { IContractOtherCostLimitSpanSelectionItem } from '../../UerProject/Contract/hooks/useUerContractOtherCostLimitSpanSelectionList';
import { SeasonItemsIdsEnum } from '../Contract/OtherCreate/commonData';

interface ILimitsDataResult {
    limit: ContractOtherCostLimitInput[];
    withError: boolean;
    errorMsgObj: Record<string, string> | null;
}
export const prepareLimitsData = (checkedLimits: Record<string, boolean>, data: any, checkMethod: boolean) => {
    const LimitsDataResult = Object.entries(checkedLimits).reduce(
        (acc: ILimitsDataResult, [key, value]) => {
            if (value) {
                if (data?.cost?.[key] && (!checkMethod || data?.method?.[key])) {
                    const distributionMethod = data?.method?.[key]?.id;
                    const limitSpanInputs = data?.limitSpanInputs?.[key];

                    let limitSpans: ContractOtherCostLimitSpanInput[] = [];
                    const seasonDistributions = [] as ContractOtherCostSeasonLimitInput[];

                    if (distributionMethod && distributionMethod === SeasonItemsIdsEnum.MANUAL) {
                        const manualDistributionData = data?.manualDistribution?.[key];

                        if (manualDistributionData) {
                            Object.entries(manualDistributionData).forEach(([seasonKey, seasonCost]) => {
                                seasonDistributions.push({
                                    //  seasonKey в объекте приходит формата season_ID
                                    seasonId: Number(seasonKey.replace('season_', '')),
                                    cost: replacerCost(seasonCost as string),
                                });
                            });
                        }

                        const manualSum = seasonDistributions.reduce(
                            (sumAcc, item) => sumAcc + replacerCost(item.cost),
                            0,
                        );

                        if (manualSum !== replacerCost(data?.cost?.[key])) {
                            acc.errorMsgObj = {
                                ...acc.errorMsgObj,
                                [key]: 'Сумма лимитов не равна Лимиту по Договору',
                            };
                            acc.withError = true;
                        }
                    }

                    if (limitSpanInputs?.length) {
                        limitSpans = limitSpanInputs
                            .filter(
                                (l: IContractOtherCostLimitSpanSelectionItem) =>
                                    l.isGroup || (l.spanMember && !l.isGroupMember),
                            )
                            .reduce(
                                (
                                    ac: ContractOtherCostLimitSpanInput[],
                                    limitSpanInput: IContractOtherCostLimitSpanSelectionItem,
                                ) => {
                                    const children = limitSpanInputs.filter(
                                        (l: IContractOtherCostLimitSpanSelectionItem) =>
                                            l.isGroupMember && l.groupKey === limitSpanInput.id,
                                    );
                                    const spanSeasons: ContractOtherCostLimitSpanSeasonInput[] = children.map(
                                        (c: IContractOtherCostLimitSpanSelectionItem) => ({
                                            contractSeasonId: c.contractSeasonId,
                                            cost: c.cost,
                                        }),
                                    );

                                    const otherConditionalData = limitSpanInput.otherCostTypeUniqueId
                                        ? { otherCostTypeUniqueId: limitSpanInput.otherCostTypeUniqueId }
                                        : { totalType: limitSpanInput.totalType };

                                    const item = {
                                        cost: limitSpanInput.cost,
                                        spanType: limitSpanInput.spanType,
                                        ...otherConditionalData,
                                        spanSeasons,
                                    };

                                    ac.push(item);

                                    return ac;
                                },
                                [],
                            );
                    }

                    acc.limit.push({
                        constructionTypeId: Number(key),
                        cost: replacerCost(data?.cost?.[key]),
                        distributionMethod,
                        ...(seasonDistributions.length ? { seasonDistributions } : {}),
                        ...(limitSpans?.length ? { limitSpans } : {}),
                    });
                } else {
                    acc.withError = true;
                }
            }

            return acc;
        },
        {
            limit: [],
            withError: false,
            errorMsgObj: null,
        },
    );

    return {
        limitsArray: LimitsDataResult.limit,
        haveError: LimitsDataResult.withError,
        errorMsgObj: LimitsDataResult.errorMsgObj,
    };
};

export const validateLimitCost =
    ({
        isSelected,
        checkNegativePercent,
        checkZero = true,
        constructionTypeId,
        hasLimitSpan,
    }: {
        isSelected: boolean;
        checkNegativePercent?: boolean;
        checkZero?: boolean;
        constructionTypeId?: number;
        hasLimitSpan?: boolean;
    }) =>
    (value: string | number | null, formValues: any) => {
        if (!value && isSelected) {
            return 'Обязательное поле';
        }

        const isPrimitive = typeof value === 'string' || typeof value === 'number';

        if (isPrimitive && isSelected) {
            if (checkZero && replacerCost(value) === 0) {
                return 'Стоимость обязательна для заполнения';
            }

            if (checkNegativePercent && replacerCost(value) < 0) {
                return 'Процент должен быть не отрицательным числом';
            }
        }

        if (hasLimitSpan && constructionTypeId) {
            const costSum =
                R.sum(
                    formValues?.limitSpanInputs?.[constructionTypeId]
                        ?.filter((i: IContractOtherCostLimitSpanSelectionItem) => i.spanMember)
                        ?.map((i: IContractOtherCostLimitSpanSelectionItem) => i.cost) || [],
                ) || 0;

            if (costSum !== replacerCost(value)) {
                return 'Требуется скорректировать распределение лимитов';
            }
        }

        return true;
    };
